import { useState, useEffect, useRef } from 'react';
import { searchAddress, type GeocodeSearchResult } from '../../lib/geocoding';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onLocationSelect?: (latitude: number, longitude: number, address: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function AddressAutocomplete({
  value,
  onChange,
  onLocationSelect,
  placeholder = 'Enter address...',
  disabled = false,
}: AddressAutocompleteProps) {
  // IMPORTANT:
  // `value` is a controlled prop and can change for reasons other than user typing
  // (e.g. initial load, switching tabs, selecting on map, refetching).
  // We only want to query the geocoder when the user types into the input.
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeocodeSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const suppressNextSearchRef = useRef(false);

  // Search for addresses only when the user updates the input (debounced).
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (suppressNextSearchRef.current) {
      suppressNextSearchRef.current = false;
      return;
    }

    if (!query || query.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      const results = await searchAddress(query);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setIsSearching(false);
      setSelectedIndex(-1);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setQuery(next);
    onChange(next);
    setShowSuggestions(true);
  };

  const handleSelect = (suggestion: GeocodeSearchResult) => {
    // Hide suggestions
    setShowSuggestions(false);
    setSuggestions([]);
    
    // If onLocationSelect is provided, use it to update everything (address + coordinates)
    // Otherwise, just update the address text via onChange
    if (onLocationSelect) {
      onLocationSelect(suggestion.latitude, suggestion.longitude, suggestion.displayName);
    } else {
      // Update the address text only
      onChange(suggestion.displayName);
    }

    // Avoid immediately re-querying when the controlled value updates from selection.
    suppressNextSearchRef.current = true;
    setQuery('');
    
    // Return focus to input
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      }
      case 'Enter': {
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      }
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // If focus is moving to a suggestion, don't hide yet and keep input focused
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && suggestionsRef.current?.contains(relatedTarget)) {
      // Keep focus on input when clicking suggestions
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return;
    }
    
    // Hide suggestions after a short delay to allow click events to fire
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Ensure input maintains focus when suggestions are shown
  useEffect(() => {
    if (showSuggestions && suggestions.length > 0 && inputRef.current) {
      // Ensure input has focus when suggestions are displayed
      if (document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [showSuggestions, suggestions.length]);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (value.length > 0 && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          className="mt-1 block w-full rounded-md border-input bg-background text-foreground shadow-sm focus:border-ring focus:ring-ring sm:text-sm px-3 py-2"
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg
              className="animate-spin h-4 w-4 text-muted-foreground"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.placeId}
              type="button"
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault();
                // Keep focus on input
                inputRef.current?.focus();
                handleSelect(suggestion);
              }}
              onClick={(e) => {
                e.preventDefault();
                // Keep focus on input
                inputRef.current?.focus();
                handleSelect(suggestion);
              }}
              onMouseEnter={() => {
                // Update selected index on hover for visual feedback
                setSelectedIndex(index);
              }}
              className={`w-full text-left px-4 py-2 hover:bg-accent focus:bg-accent focus:outline-none ${
                index === selectedIndex ? 'bg-accent' : ''
              }`}
            >
              <div className="text-sm text-popover-foreground">{suggestion.displayName}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {suggestion.latitude.toFixed(6)}, {suggestion.longitude.toFixed(6)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
